/**
 * Gets the user's geolocation only if permission has already been granted.
 * This avoids triggering a browser permission prompt, which adds unnecessary
 * friction during checkout — especially for redirect-based payment methods.
 *
 * Note: navigator.permissions.query() is not supported in Safari/WebKit browsers.
 * In those environments, this function silently returns undefined rather than
 * prompting the user, which is the desired behavior for checkout flows.
 *
 * Mirrors the approach used in sdk-web's getGeolocationIfEnabled().
 */
export async function getGeolocationIfEnabled(): Promise<GeolocationPosition | undefined> {
  try {
    if (!navigator?.permissions || !navigator.geolocation) {
      console.debug('[geolocation] Permissions API or Geolocation API not available')
      return undefined
    }

    const permissionStatus = await navigator.permissions.query({
      name: 'geolocation' as PermissionName,
    })

    if (permissionStatus.state !== 'granted') {
      console.debug(`[geolocation] Permission state: ${permissionStatus.state}, skipping`)
      return undefined
    }

    return new Promise<GeolocationPosition | undefined>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        () => {
          console.debug('[geolocation] getCurrentPosition failed after permission was granted')
          resolve(undefined)
        },
        { timeout: 8000 }
      )
    })
  } catch {
    console.debug('[geolocation] Error querying permissions')
    return undefined
  }
}
